(() => {
  const OVERCLOCK_SEQUENCE = [
    'up',
    'up',
    'down',
    'down',
    'left',
    'right',
    'left',
    'right',
    'b',
    'a',
    'start',
  ];

  const OVERCLOCK_RESET_DELAY_MS = 2500;
  const OVERCLOCK_DURATION_MS = 15000;
  const OVERCLOCK_LINES = [
    'Cheat mode: retro terminal overclock',
    'Input profile: keyboard adapter online',
    'Flavor multiplier: x9000 engaged',
    'Access tier: classified snack protocol',
    'Status: overclock active',
  ];

  const terminalSnapshots = new WeakMap();
  let isOverclockActive = false;
  let overclockTimeoutId = null;

  const parseTerminalLineText = (lineText) => {
    const colonIndex = lineText.indexOf(':');

    if (colonIndex === -1) {
      return {
        labelText: lineText,
        valueText: '',
      };
    }

    return {
      labelText: lineText.slice(0, colonIndex + 1),
      valueText: lineText.slice(colonIndex + 1),
    };
  };

  const renderLineText = (line, lineText) => {
    const labelNode = line.querySelector('.terminal__label');
    const valueNode = line.querySelector('.terminal__value');
    const { labelText, valueText } = parseTerminalLineText(lineText);

    line.setAttribute('data-line-text', lineText);
    line.classList.add('is-visible');
    line.classList.remove('is-typing', 'terminal__line--active');

    if (labelNode) {
      labelNode.textContent = labelText;
    }

    if (valueNode) {
      valueNode.textContent = valueText;
    }
  };

  const getTerminalLines = (terminal) => {
    const terminalBody = terminal.querySelector('[data-terminal-body]');

    if (!terminalBody) {
      return [];
    }

    return Array.from(terminalBody.querySelectorAll('[data-terminal-line]'));
  };

  const measureExpandedTerminalBodyHeight = (terminal, lines) => {
    const terminalBody = terminal.querySelector('[data-terminal-body]');

    if (!terminalBody) {
      return 0;
    }

    const probe = terminalBody.cloneNode(true);
    const originalWidth = terminalBody.getBoundingClientRect().width;

    probe.setAttribute('aria-hidden', 'true');
    probe.style.position = 'absolute';
    probe.style.visibility = 'hidden';
    probe.style.pointerEvents = 'none';
    probe.style.left = '0';
    probe.style.top = '0';
    probe.style.width = `${originalWidth}px`;
    probe.style.minHeight = '0';
    probe.style.height = 'auto';
    probe.style.maxHeight = 'none';

    const probeLines = Array.from(
      probe.querySelectorAll('[data-terminal-line]'),
    );

    probeLines.forEach((line, index) => {
      renderLineText(line, lines[index]?.getAttribute('data-line-text') || '');
    });

    terminal.append(probe);

    const measuredHeight = probe.getBoundingClientRect().height;

    probe.remove();

    return measuredHeight;
  };

  const takeTerminalSnapshot = (terminal, lines) => {
    if (terminalSnapshots.has(terminal)) {
      return;
    }

    const titleNode = terminal.querySelector('.terminal__title');

    terminalSnapshots.set(terminal, {
      title: titleNode ? titleNode.textContent || '' : '',
      lines: lines.map((line) => line.getAttribute('data-line-text') || ''),
    });
  };

  const applyOverclockToTerminal = (terminal) => {
    const lines = getTerminalLines(terminal);

    if (!lines.length) {
      return;
    }

    takeTerminalSnapshot(terminal, lines);

    const titleNode = terminal.querySelector('.terminal__title');

    if (titleNode) {
      titleNode.textContent = 'Overclock mode';
    }

    lines.forEach((line, index) => {
      const nextLineText = OVERCLOCK_LINES[index] || '';
      renderLineText(line, nextLineText);

      if (index === lines.length - 1) {
        line.classList.add('terminal__line--active');
      }
    });

    terminal.classList.add('terminal--overclock');
  };

  const restoreTerminalSnapshot = (terminal) => {
    const snapshot = terminalSnapshots.get(terminal);
    const lines = getTerminalLines(terminal);

    if (!snapshot || !lines.length) {
      terminal.classList.remove('terminal--overclock');
      return;
    }

    const titleNode = terminal.querySelector('.terminal__title');

    if (titleNode) {
      titleNode.textContent = snapshot.title;
    }

    lines.forEach((line, index) => {
      const lineText = snapshot.lines[index] || '';
      renderLineText(line, lineText);

      if (index === lines.length - 1) {
        line.classList.add('terminal__line--active');
      }
    });

    terminal.classList.remove('terminal--overclock');
    terminalSnapshots.delete(terminal);
  };

  const showOverclockToast = () => {
    const activeToast = document.querySelector('[data-overclock-toast]');

    if (activeToast) {
      activeToast.remove();
    }

    const toast = document.createElement('div');
    toast.className = 'terminal-overclock-toast';
    toast.setAttribute('data-overclock-toast', 'true');
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    toast.textContent = 'Retro terminal overclock activated';

    document.body.append(toast);

    window.setTimeout(() => {
      toast.classList.add('is-visible');
    }, 10);

    window.setTimeout(() => {
      toast.classList.remove('is-visible');
      window.setTimeout(() => {
        toast.remove();
      }, 300);
    }, 2600);
  };

  const deactivateOverclock = () => {
    const terminals = document.querySelectorAll('[data-terminal]');

    terminals.forEach((terminal) => {
      restoreTerminalSnapshot(terminal);
    });

    document.documentElement.classList.remove('terminal-overclock-active');
    isOverclockActive = false;
  };

  const activateOverclock = () => {
    if (isOverclockActive) {
      if (overclockTimeoutId) {
        window.clearTimeout(overclockTimeoutId);
      }
    }

    const terminals = document.querySelectorAll('[data-terminal]');

    terminals.forEach((terminal) => {
      applyOverclockToTerminal(terminal);
    });

    document.documentElement.classList.add('terminal-overclock-active');
    isOverclockActive = true;
    showOverclockToast();

    overclockTimeoutId = window.setTimeout(() => {
      deactivateOverclock();
    }, OVERCLOCK_DURATION_MS);
  };

  const createSequenceMatcher = ({ sequence, onMatch, resetDelayMs }) => {
    let progress = 0;
    let resetTimeoutId = null;

    const reset = () => {
      progress = 0;

      if (resetTimeoutId) {
        window.clearTimeout(resetTimeoutId);
        resetTimeoutId = null;
      }
    };

    const scheduleReset = () => {
      if (resetTimeoutId) {
        window.clearTimeout(resetTimeoutId);
      }

      resetTimeoutId = window.setTimeout(() => {
        reset();
      }, resetDelayMs);
    };

    const push = (token) => {
      if (token === sequence[progress]) {
        progress += 1;
        scheduleReset();

        if (progress === sequence.length) {
          reset();
          onMatch();
        }

        return;
      }

      if (token === sequence[0]) {
        progress = 1;
        scheduleReset();
        return;
      }

      reset();
    };

    return {
      push,
      reset,
    };
  };

  const normalizeKeyboardToken = (event) => {
    if (
      event.defaultPrevented ||
      event.metaKey ||
      event.ctrlKey ||
      event.altKey
    ) {
      return null;
    }

    const key = event.key;

    if (key === 'ArrowUp') return 'up';
    if (key === 'ArrowDown') return 'down';
    if (key === 'ArrowLeft') return 'left';
    if (key === 'ArrowRight') return 'right';
    if (key === 'Enter') return 'start';
    if (key.length === 1 && key.toLowerCase() === 'a') return 'a';
    if (key.length === 1 && key.toLowerCase() === 'b') return 'b';

    return null;
  };

  const createKeyboardInputAdapter = ({ onToken }) => {
    const onKeyDown = (event) => {
      const target = event.target;

      if (
        target instanceof HTMLElement &&
        (target.isContentEditable ||
          target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT')
      ) {
        return;
      }

      const token = normalizeKeyboardToken(event);

      if (!token) {
        return;
      }

      onToken(token);
    };

    document.addEventListener('keydown', onKeyDown);

    return {
      teardown: () => {
        document.removeEventListener('keydown', onKeyDown);
      },
    };
  };

  const initializeOverclockListener = () => {
    if (window.__mxOverclockListenerReady) {
      return;
    }

    window.__mxOverclockListenerReady = true;

    const matcher = createSequenceMatcher({
      sequence: OVERCLOCK_SEQUENCE,
      onMatch: activateOverclock,
      resetDelayMs: OVERCLOCK_RESET_DELAY_MS,
    });

    // Input adapters are modular so touch/gesture support can be added later.
    createKeyboardInputAdapter({
      onToken: (token) => {
        matcher.push(token);
      },
    });
  };

  const renderStaticTerminal = (terminal, lines) => {
    lines.forEach((line, index) => {
      const lineText = line.getAttribute('data-line-text') || '';
      renderLineText(line, lineText);

      if (index === lines.length - 1) {
        line.classList.add('terminal__line--active');
      }
    });
  };

  const setupTerminal = (terminal) => {
    if (!terminal || terminal.dataset.terminalReady === 'true') {
      return;
    }

    const terminalBody = terminal.querySelector('[data-terminal-body]');

    if (!terminalBody) {
      return;
    }

    const lines = Array.from(
      terminalBody.querySelectorAll('[data-terminal-line]'),
    );

    if (!lines.length) {
      return;
    }

    terminal.dataset.terminalReady = 'true';

    if (isOverclockActive) {
      applyOverclockToTerminal(terminal);
      return;
    }

    if (terminal.closest('.cart-page--live-update')) {
      renderStaticTerminal(terminal, lines);
      return;
    }

    // Reserve the final wrapped height before animation starts to avoid layout shifting.
    terminalBody.style.minHeight = `${Math.max(
      terminalBody.scrollHeight,
      measureExpandedTerminalBodyHeight(terminal, lines),
    )}px`;

    const lineDelay = 420;
    const charDelay = 24;
    const lineBreakDelay = 320;

    terminal.classList.add('terminal--typing');

    lines.forEach((line) => {
      const lineText = line.getAttribute('data-line-text') || '';
      const labelNode = line.querySelector('.terminal__label');
      const valueNode = line.querySelector('.terminal__value');

      line.classList.remove('terminal__line--active');

      if (labelNode) labelNode.textContent = '';
      if (valueNode) valueNode.textContent = '';

      const colonIndex = lineText.indexOf(':');
      if (colonIndex !== -1) {
        line.dataset.labelText = lineText.slice(0, colonIndex + 1);
        line.dataset.valueText = lineText.slice(colonIndex + 1);
      } else {
        line.dataset.labelText = lineText;
        line.dataset.valueText = '';
      }

      line.dataset.fullText = lineText;
    });

    const typeLine = (lineIndex) => {
      const line = lines[lineIndex];

      if (!line) {
        return;
      }

      const labelNode = line.querySelector('.terminal__label');
      const valueNode = line.querySelector('.terminal__value');
      const labelText = line.dataset.labelText || '';
      const valueText = line.dataset.valueText || '';
      const fullText = line.dataset.fullText || '';
      let charIndex = 0;

      line.classList.add('is-visible', 'is-typing');

      const typeCharacter = () => {
        if (charIndex <= fullText.length) {
          const currentPos = charIndex;
          const labelLength = labelText.length;

          if (labelNode) {
            labelNode.textContent = labelText.slice(
              0,
              Math.min(currentPos, labelLength),
            );
          }

          if (valueNode) {
            const valueStart = Math.max(0, currentPos - labelLength);
            valueNode.textContent = valueText.slice(0, valueStart);
          }

          charIndex += 1;
          window.setTimeout(typeCharacter, charDelay);
          return;
        }

        line.classList.remove('is-typing');

        if (lineIndex === lines.length - 1) {
          line.classList.add('terminal__line--active');
          return;
        }

        window.setTimeout(() => {
          typeLine(lineIndex + 1);
        }, lineBreakDelay);
      };

      window.setTimeout(typeCharacter, lineDelay);
    };

    typeLine(0);
  };

  const initializeTerminals = (scope = document) => {
    const terminals = scope.querySelectorAll('[data-terminal]');
    terminals.forEach((terminal) => setupTerminal(terminal));
  };

  window.initializeTerminals = initializeTerminals;

  document.addEventListener('DOMContentLoaded', () => {
    initializeOverclockListener();
    initializeTerminals(document);
  });

  document.addEventListener('shopify:section:load', (event) => {
    initializeTerminals(event.target);
  });
})();
